// @flow

import React, { type Node, useMemo } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsSelect, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUserInputValidationError } from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';

import { Select, SelectOption } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import {
  useInvoiceDocumentPreservation,
} from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/customer-form/use-invoice-document-preservation';
import { useArgentineEwalletDocumentForceShow } from './use-argentine-ewallet-document-force-show';

export function FieldArgentineDocumentType(): Node {
  const [value, handleChange] = useUserInputAsSelect('argentineDocumentType');
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [documentValue, setDocument] = useUserInputValue('argentineDocument');
  const submitting = useSubmitting();
  const inputId = useUniqueId();

  const validationError = useUserInputValidationError('argentineDocumentType');
  const formHasBeenSent = useFormHasBeenSent();

  const hasDocumentPrefilled = useInvoiceDocumentPreservation(documentValue, setDocument, value);

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('document');
  const forceShow = useArgentineEwalletDocumentForceShow();

  const documentTypes = useMemo(() => getDocumentTypes(paymentMethod), [paymentMethod]);

  if (isOptionalFieldByPaymentType && !forceShow) {
    return null;
  }

  return (
    <div data-test-name="customer-document-type">
      <label htmlFor={inputId}>
        {getLabel(hasDocumentPrefilled)}
      </label>
      <Select
        id={inputId}
        value={value}
        onChange={handleChange}
        readOnly={submitting}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="customer-document-type"
      >
        {documentTypes.map(documentType => (
          <SelectOption key={documentType.value} value={documentType.value}>
            {documentType.label}
          </SelectOption>
        ))}
      </Select>

      {formHasBeenSent && validationError && (
        <ErrorLabel>El documento elijido es inválido</ErrorLabel>
      )}
    </div>
  );
}

const AR_DOCUMENT_TYPES = [
  { value: '', label: 'Elige un documento' },
  { value: 'CUIT', label: 'CUIT' },
  { value: 'CUIL', label: 'CUIL' },
  { value: 'CDI', label: 'CDI' },
  { value: 'DNI', label: 'DNI' },
];

function keepOnlyDocumentTypesForDebin(): { value: string, label: string }[] {
  return AR_DOCUMENT_TYPES.filter(docType => ['Elige un documento', 'CUIT', 'CUIL'].includes(docType.label));
}

function getDocumentTypes(paymentMethod): { value: string, label: string }[] {
  switch (paymentMethod) {
    case 'banktransfer':
      return keepOnlyDocumentTypesForDebin();
    default:
      return AR_DOCUMENT_TYPES;
  }
}

function getLabel(hasPrefilledDocument: boolean): string {
  return hasPrefilledDocument ? 'Elija el tipo de documento según lo indicado en el campo abajo' : 'Tipo de identificación';
}
